import {
  ControlListProps,
  useRuntimeEnv,
  useTranslations,
} from "@harea/component";
import { useDateIncludeWorkingDays } from "../../../../../hooks/useDateIncludeWorkingDays";
import moment from "moment";
import { useTimeOffTypeList } from "../../../../../views/create/hooks/useTimeOffTypeList";
import EmployeeListControl from "../components/EmployeeListControl";

export const useBulkTimeOffControlForm = (): ControlListProps => {
  const [
    END_DATE_ERROR,
    START_DATE_ERROR,
    INCLUDE_WORKING_DAYS_ERROR,
    DATE_BEGIN,
    HOUR_BEGIN,
    DATE_END,
    HOUR_END,
    EMPLOYEES,
    LABELS,
    REASON,
    SAVE,
    CANCEL,
    IS_CRITICAL_DAY
  ] = useTranslations(i18nKeys);

  const { runtimeEnv } = useRuntimeEnv(true);
  const workingHourBegin =
    runtimeEnv?.organisation?.profile?.workHourBegin ?? "06:00";
  const workingHourEnd =
    runtimeEnv?.organisation?.profile?.workHourEnd ?? "17:00";
  const workRestHourEnd =
    runtimeEnv?.organisation?.profile?.workRestHourEnd ?? "13:00";

  const timeOffTypes = useTimeOffTypeList("LEAVE");
  const includeWorkingDays = useDateIncludeWorkingDays();

  const handleValidate = (name: string, value: any, formData_: any) => {
    if (name === "dateBegin") {
      const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
      if (isCriticalDay) {
        return IS_CRITICAL_DAY
      }
      if (!isWorkingDay) {
        return INCLUDE_WORKING_DAYS_ERROR;
      }
      const dateBeginIsAfterDateEnd = moment(formData_?.dateBegin)?.isAfter(
        moment(formData_?.dateEnd),
      );
      if (formData_?.dateBegin && dateBeginIsAfterDateEnd) {
        return START_DATE_ERROR;
      }
    }
    if (name === "dateEnd") {
      const { isWorkingDay, isCriticalDay } = includeWorkingDays(moment(value))
      if (isCriticalDay) {
        return IS_CRITICAL_DAY
      }
      if (!isWorkingDay) {
        return INCLUDE_WORKING_DAYS_ERROR;
      }

      const endDateIsBefore = moment(formData_?.dateEnd).isBefore(
        moment(formData_?.dateBegin),
      );
      if (formData_?.dateBegin && endDateIsBefore) {
        return END_DATE_ERROR;
      }
    }
  };

  return {
    data: {
      forms: [
        [
          {
            type: "date-picker",
            name: "dateBegin",
            label: DATE_BEGIN,
            required: true,
            autoComplete: "off",
          },
          {
            type: "hour",
            name: "hourBegin",
            interval: 60,
            minHour: workingHourBegin,
            maxHour: workingHourEnd,
            value: workingHourBegin,
            label: HOUR_BEGIN,
          },
        ],
        [
          {
            type: "date-picker",
            name: "dateEnd",
            label: DATE_END,
            required: true,
            autoComplete: "off",
          },
          {
            type: "hour",
            name: "hourEnd",
            options: [
              { label: workingHourBegin, value: workingHourBegin },
              { label: workRestHourEnd, value: workRestHourEnd },
            ],
            value: workingHourBegin,
            label: HOUR_END,
          },
        ],
        {
          type: "employees",
          label: EMPLOYEES,
          name: "employees",
        },
        {
          type: "lov-select",
          multiple: true,
          name: "labels",
          label: LABELS,
          tp: "APPOINTMENT_LABELS",
        },
        {
          type: "textarea",
          name: "reason",
          label: REASON,
          required: true,
        },
      ],
      fields: {
        employees: EmployeeListControl,
      },
      value: {},
      next: {
        label: SAVE,
        className: "btn-primary ",
      },
      back: {
        label: CANCEL,
      },
      graphQL: {
        entity: "timeoff",
        multiple: true,
      },
    },
    validate: handleValidate,
    onBeforeSaving: (value) => {
      const type = timeOffTypes?.find((type) => type.value === "LEAVE");

      const start = moment(value?.dateBegin);
      const end = moment(value?.dateEnd);
      const totalDays = end.diff(start, "days") + 1;
      const solde = totalDays;

      return value.employees.map((employeeId) => ({
        employee: { id: employeeId },
        solde,
        type: { id: type.id },
        dateBegin: value?.dateBegin,
        hourStart: value?.hourBegin
          ? value?.hourBegin
          : (runtimeEnv?.organisation?.profile?.workHourBegin ?? "06:00"),
        dateEnd: value?.dateEnd,
        hourEnd: value?.hourEnd
          ? value?.hourEnd
          : (runtimeEnv?.organisation?.profile?.workHourBegin ?? "06:00"),
        status: "new",
        labels: value?.labels,
        reason: value?.reason,
      }));
    },
    id: ADD_BULK_TIMEOFF_FORM_ID,
  };
};

const i18nKeys = [
  "BSMRH.Calendar.TimeOff.Create.Form.EndDate.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.StartDate.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.IncludeWorkingDays.Error.Message",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentBegin",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentBegin",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.DateAppointmentEnd",
  "BSMRH.Calendar.TimeOff.Create.Form.Label.HourAppointmentEnd",
  "std_employees",
  "std_labels",
  "std_reason",
  "std_save",
  "std_cancel",
  "BSMRH.Calendar.TimeOff.Create.Form.IsCriticalDay.Error.Message",
];

const ADD_BULK_TIMEOFF_FORM_ID = "add-bulk-timeoff-form";
